import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { FormCheckbox } from "ui-kit/SmartForm";
import { useForm } from "react-final-form";
import { Divider } from "ui-kit/Divider";
import { Button } from "ui-kit/Button";
import { Modal } from "ui-kit/Modal";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Checkbox } from "ui-kit/PrimitiveControls/Checkbox";
import { Platform } from "types/platform";
import { PlatformGroup } from "../hooks/usePlatformGroups";
import { DEFAULT_PLATFORM_GROUP } from "./constants";
import IntlStore from "stories/IntlStore";
import { FormSelect } from "./FormSelect";
import { InfoTooltip } from "ui-kit/Tooltip/InfoTooltip";
import { Tooltip } from "primereact/tooltip";
import { Checkbox as PrimereactCheckbox, CheckboxChangeEvent } from "primereact/checkbox";

interface PlatformsProps {
  platformGroupsCreating: boolean;
  mainPlatformList: Platform[];
  platformGroups: PlatformGroup[];
  createPlatformGroup: (name: string, ids: string[]) => Promise<void>;
  removePlatformGroup: (id: string) => Promise<void>;
  platforms?: Platform[];
  setSelectedValues: (selectedValues: string[]) => void;
  selectedValues: string[];
  upDatePlatformGroup: (groupId: string, name: string, ids: number[]) => Promise<void>;
  setValidButton: (valid: boolean) => void;
  ugcPlatforms?: any;
}

export const Platforms: React.FC<PlatformsProps> = props => {
  const {
    mainPlatformList,
    platformGroups,
    platformGroupsCreating,
    createPlatformGroup,
    removePlatformGroup,
    platforms,
    selectedValues,
    setSelectedValues,
    upDatePlatformGroup,
    setValidButton,
    ugcPlatforms: initialUgcPlatforms = []
  } = props;

  const { change, getState } = useForm();
  const form = useForm();

  const [isOpen, setIsOpen] = useState(false);
  const [newPlatformGroupName, setNewPlatformGroupName] = useState("");
  const [selectedValuesData, setSelectedValuesData] = useState<Platform[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [editValueId, setEditValueId] = useState<string>("");

  useEffect(() => {
    setEditValueId(platformGroups?.find(val => val.name === editId)?.id || "")
  }, [selectedValuesData, editId]);


  const activePlatformGroup = getState().values.platformGroup;

  const [newPlatformGroupCheckboxes, setNewPlatformGroupCheckboxes] = useState<Record<string, boolean>>(() => {
    const values: Record<string, boolean> = { all: false };
    mainPlatformList.forEach(p => {
      values[p.id] = false;
    });
    return values;
  });

  const YT_ID = mainPlatformList?.find(val => val.name === "YouTube Content ID")?.id ?? "";

  const ugcPlatforms = useMemo(() => initialUgcPlatforms.length ? initialUgcPlatforms : mainPlatformList.filter(p => p.ugc), [mainPlatformList, initialUgcPlatforms]);
  const regularPlatforms = useMemo(() => mainPlatformList.filter(p => !p.ugc), [mainPlatformList]);

  const items = useMemo(() => {
    return [
      { value: DEFAULT_PLATFORM_GROUP, label: <FormattedMessage id="NewRelease.Publish.All" defaultMessage="All" /> },
      ...platformGroups.map(group => ({ value: group.id, label: group.name }))
    ];
  }, [platformGroups]);

  const flattenedFromAll: { id: string; name: string }[] = [];
  if (activePlatformGroup === "all") {
    platformGroups.forEach(group => {
      group.platforms.forEach(p => {
        flattenedFromAll.push({ id: p.id, name: p.name });
      });
    });
  }

  useEffect(() => {
    if (!selectedValues || selectedValues.length === 0) {
      setSelectedValuesData([]);
      return;
    }

    const result: Platform[] = selectedValues.reduce((acc: Platform[], valueId) => {
      const group = platformGroups.find(pg => pg.id === valueId);
      if (group) {
        group.platforms.forEach(platform => {
          if (!acc.some(p => p.id === platform.id)) acc.push(platform);
        });
      }
      return acc;
    }, []);
    setSelectedValuesData(result);
  }, [selectedValues, platformGroups, editId]);




  useEffect(() => {
    const result: Platform[] = editValue.reduce((acc: Platform[], valueId) => {
      const group = platformGroups.find(pg => pg.id === valueId);
      if (group) {
        group.platforms.forEach(platform => {
          if (!acc.some(p => p.id === platform.id)) acc.push(platform);
        });
      }
      return acc;
    }, []);
    setSelectedValuesData(result);
  }, [editValue, platformGroups, editId]);

  const prevSelectedValuesRef = useRef<string[] | null>(null);
  
  useEffect(() => {
    if (!platforms || !platforms.length) return;

    const prev = prevSelectedValuesRef.current;
    const prevSet = prev ? new Set(prev) : new Set<string>();
    const currentSet = new Set(selectedValues);

    const sameSize = prevSet.size === currentSet.size;
    let sameContent = sameSize;
    if (sameContent) {
      currentSet.forEach(v => {
        if (!prevSet.has(v)) sameContent = false;
      });
    }

    if (sameContent) return;

    prevSelectedValuesRef.current = [...selectedValues];

    const formState = getState();
    const initialPlatforms: Record<string, boolean> = formState.initialValues?.platforms || {};

    if (!selectedValues.length) {
      // Restore previous (release) values when group selection is cleared
      platforms.forEach(p => {
        const key = `platform-${p.id}`;
        const initialChecked = !!initialPlatforms[key];
        change(`platforms.${key}`, initialChecked);
      });
      return;
    }

    // A group (or several) is selected: compute union of all selected groups and preset checkboxes from that
    const selectedPlatforms: Platform[] = [];
    selectedValues.forEach(valueId => {
      const group = platformGroups.find(pg => pg.id === valueId);
      if (group) {
        group.platforms.forEach(platform => {
          if (!selectedPlatforms.some(p => p.id === platform.id)) {
            selectedPlatforms.push(platform);
          }
        });
      }
    });

    platforms.forEach(p => {
      const key = `platform-${p.id}`;
      const isSelected = selectedPlatforms.some(v => v.id === p.id);
      change(`platforms.${key}`, isSelected);
    });
  }, [selectedValues, platforms, change, getState, platformGroups]);


  useEffect(() => {
    if (editId) setNewPlatformGroupName(editId);
  }, [editId]);

  useEffect(() => {
    selectedValues.length && form.change("checkAllCheckboxes", !selectedValues.length ? true : false);
  }, [selectedValues, form]);

  useEffect(() => {
    const isYouTubeSelected = newPlatformGroupCheckboxes[YT_ID] || selectedValuesData.some(v => v.id === YT_ID);

    if (!isYouTubeSelected) {
      setNewPlatformGroupCheckboxes(prev => {
        const values = { ...prev };
        mainPlatformList.forEach(p => {
          if (p.ugc && p.id !== YT_ID) values[p.id] = false;
        });
        const same = Object.keys(values).every(k => values[k] === prev[k]);
        return same ? prev : values;
      });
    }
  }, [selectedValuesData, mainPlatformList, YT_ID, newPlatformGroupCheckboxes]);


  useEffect(() => {
    if (!isOpen) return;

    const initial: Record<string, boolean> = { all: false };
    mainPlatformList.forEach(p => {
      const isChecked = selectedValuesData.some(v => v.id === p.id);
      initial[p.id] = isChecked;
    });
    initial.all = Object.keys(initial).every(k => k === "all" || initial[k]);
    setNewPlatformGroupCheckboxes(initial);
  }, [isOpen, editId, selectedValuesData, mainPlatformList]);

  const isSaveDisabled = editId
    ? newPlatformGroupName === editId &&
    !Object.entries(newPlatformGroupCheckboxes).some(([k, v]) => k !== "all" && v)
    : newPlatformGroupName.length < 3 ||
    newPlatformGroupName.length > 30 ||
    !Object.entries(newPlatformGroupCheckboxes).some(([k, v]) => k !== "all" && v);

  const onSaveNewPlatformGroupCheckboxes = async () => {
    const ids = Object.entries(newPlatformGroupCheckboxes)
      .filter(([k, v]) => k !== "all" && v)
      .map(([k]) => k);

    if (editId) {
      const data = [...ids].map(v => {
        const num = Number(v);
        return isNaN(num) ? null : num;
      });
      setSelectedValuesData([]);
      await upDatePlatformGroup(editValueId, newPlatformGroupName === editId ? '' : newPlatformGroupName, data as number[]);
    } else {
      await createPlatformGroup(newPlatformGroupName, ids);
    }

    setSelectedValuesData([]);
    setSelectedValues([]);
    setNewPlatformGroupName("");
    setIsOpen(false);
    setEditId(null);
    setEditValue([]);
    setNewPlatformGroupCheckboxes(() => {
      const values: Record<string, boolean> = { all: false };
      mainPlatformList.forEach(p => {
        values[p.id] = false;
      });
      return values;
    });
    setDropdownOpen(false);
    form.reset({
      platforms: {},
      checkAllCheckboxes: false,
      platformGroup: undefined,
    });

  };


  const values = getState().values;

  const hasTrue = Object.values(values.platforms).some(value => value === true);

  useEffect(() => {
    setValidButton(hasTrue);
  }, [hasTrue]);



  return (
    <Container id="platforms">
      <PlatformMethodContainer>
        <PlatformGroupSelectContainer>
          <Title>
            Select a platform{" "}
            <InfoTooltip name={`select-tooltip-${"Select a platform"}`} tooltip={null} />
          </Title>
          <FormSelect
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}
            setEditValue={setEditValue}
            editValue={editValue}
            editIcon={true}
            setEditId={setEditId}
            text="DSP Groups"
            name="platformGroup"
            placeholder="DSP Groups"
            items={items
              .filter(val => val.value !== "all")
              .map(item => ({ ...item, label: typeof item.label === "string" ? item.label : "" }))}
            label={<FormattedMessage id="NewRelease.Publish.SelectMethod" defaultMessage="Select a method" />}
            itemTemplate={option => (
              <PlatformOption>
                <PlatformOptionLabel>{option.label}</PlatformOptionLabel>
                {option.value !== DEFAULT_PLATFORM_GROUP && (
                  <div onClick={() => removePlatformGroup(option.value)}>
                    <svg />
                  </div>
                )}
              </PlatformOption>
            )}
            tooltip="Tooltip"
            setIsOpen={setIsOpen}
            removePlatformGroup={removePlatformGroup}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
          />
        </PlatformGroupSelectContainer>

        <AddGroupButton
          onClick={() => {
            setIsOpen(true);
            setEditId(null);
            setSelectedValuesData([]);
            setNewPlatformGroupName("");
          }}
        >
          <svg width="106" height="18" viewBox="0 0 106 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.5566 14L22.7759 11.6323H19.4053L18.6245 14H16.936L20.186 4.84033H22.0332L25.2832 14H23.5566ZM21.0684 6.45898L19.7861 10.3691H22.3887L21.1128 6.45898H21.0684ZM28.4062 14.1079C26.7114 14.1079 25.6133 12.7686 25.6133 10.6294C25.6133 8.50293 26.7114 7.15723 28.4062 7.15723C29.3711 7.15723 30.1328 7.65869 30.4883 8.40137H30.5264V4.84033H32.1069V14H30.5581V12.8511H30.5327C30.1646 13.6064 29.3901 14.1079 28.4062 14.1079ZM28.8823 8.4585C27.8667 8.4585 27.2319 9.30273 27.2319 10.6357C27.2319 11.9751 27.8604 12.813 28.8823 12.813C29.8789 12.813 30.5327 11.9688 30.5327 10.6357C30.5327 9.31543 29.8789 8.4585 28.8823 8.4585ZM35.9473 14.1079C34.2524 14.1079 33.1543 12.7686 33.1543 10.6294C33.1543 8.50293 34.2524 7.15723 35.9473 7.15723C36.9121 7.15723 37.6738 7.65869 38.0293 8.40137H38.0674V4.84033H39.6479V14H38.0991V12.8511H38.0737C37.7056 13.6064 36.9312 14.1079 35.9473 14.1079ZM36.4233 8.4585C35.4077 8.4585 34.7729 9.30273 34.7729 10.6357C34.7729 11.9751 35.4014 12.813 36.4233 12.813C37.4199 12.813 38.0737 11.9688 38.0737 10.6357C38.0737 9.31543 37.4199 8.4585 36.4233 8.4585ZM43.7358 4.84033H47.0811C49.8169 4.84033 51.4355 6.49707 51.4355 9.39795C51.4355 12.2988 49.8232 14 47.0811 14H43.7358V4.84033ZM45.3735 6.22412V12.6162H46.9097C48.7505 12.6162 49.7661 11.4927 49.7661 9.4043C49.7661 7.354 48.7378 6.22412 46.9097 6.22412H45.3735ZM52.3623 11.4229H53.9492C54.0508 12.2671 54.895 12.8193 56.0122 12.8193C57.1167 12.8193 57.8848 12.2734 57.8848 11.5117C57.8848 10.8516 57.415 10.4707 56.2598 10.1978L55.0728 9.91846C53.3843 9.53125 52.5845 8.71875 52.5845 7.41113C52.5845 5.78613 54 4.68799 55.9932 4.68799C58.0117 4.68799 59.3447 5.77979 59.3828 7.39209H57.8213C57.7451 6.53516 57.0278 6.021 55.9805 6.021C54.9458 6.021 54.2476 6.52881 54.2476 7.29053C54.2476 7.8999 54.7109 8.25537 55.8345 8.52197L56.8882 8.76318C58.748 9.18848 59.5352 9.95654 59.5352 11.3213C59.5352 13.0605 58.1387 14.1523 55.9233 14.1523C53.8159 14.1523 52.4321 13.1113 52.3623 11.4229ZM60.7729 4.84033H64.334C66.1558 4.84033 67.4189 6.07812 67.4189 7.90625C67.4189 9.73438 66.1177 10.9722 64.2642 10.9722H62.4106V14H60.7729V4.84033ZM62.4106 6.17969V9.65186H63.896C65.0703 9.65186 65.7559 9.01709 65.7559 7.90625C65.7559 6.80811 65.0767 6.17969 63.9023 6.17969H62.4106ZM79.2383 10.1787C79.2383 12.6606 77.7529 14.1523 75.2456 14.1523C72.5732 14.1523 70.9102 12.3369 70.9102 9.38525C70.9102 6.47803 72.5669 4.68799 75.2139 4.68799C77.2959 4.68799 78.8701 5.91943 79.1685 7.75391H77.5054C77.1943 6.7002 76.3438 6.07812 75.2202 6.07812C73.5698 6.07812 72.5859 7.30322 72.5859 9.36621C72.5859 11.4736 73.6143 12.7622 75.2646 12.7622C76.6484 12.7622 77.6069 11.8862 77.6323 10.604L77.6387 10.4517H75.4297V9.20117H79.2383V10.1787ZM80.4189 14V7.27148H81.936V8.4458H81.9678C82.1963 7.62695 82.7866 7.15723 83.5801 7.15723C83.7832 7.15723 83.9609 7.19531 84.0752 7.2207V8.64893C83.9419 8.59814 83.7007 8.56006 83.4341 8.56006C82.5391 8.56006 81.9995 9.1377 81.9995 10.0835V14H80.4189ZM87.6108 14.1333C85.6367 14.1333 84.3545 12.8193 84.3545 10.6357C84.3545 8.46484 85.6494 7.13184 87.6108 7.13184C89.5723 7.13184 90.8672 8.4585 90.8672 10.6357C90.8672 12.8193 89.585 14.1333 87.6108 14.1333ZM87.6108 12.8828C88.6138 12.8828 89.2612 12.0703 89.2612 10.6357C89.2612 9.20752 88.6138 8.38867 87.6108 8.38867C86.6143 8.38867 85.9604 9.20752 85.9604 10.6357C85.9604 12.0703 86.6079 12.8828 87.6108 12.8828ZM97.8496 7.27148V14H96.3262V12.832H96.2944C95.939 13.6509 95.2598 14.1206 94.2378 14.1206C92.7778 14.1206 91.8511 13.1748 91.8511 11.6196V7.27148H93.4316V11.3022C93.4316 12.2671 93.9014 12.7876 94.79 12.7876C95.7041 12.7876 96.269 12.1401 96.269 11.1626V7.27148H97.8496ZM102.839 7.15723C104.546 7.15723 105.632 8.48389 105.632 10.6357C105.632 12.7812 104.553 14.1079 102.864 14.1079C101.887 14.1079 101.125 13.6382 100.757 12.8828H100.725V16.2153H99.1445V7.27148H100.687V8.43311H100.719C101.1 7.65234 101.861 7.15723 102.839 7.15723ZM102.356 12.813C103.378 12.813 104.013 11.9688 104.013 10.6357C104.013 9.30908 103.378 8.4585 102.356 8.4585C101.373 8.4585 100.719 9.32812 100.712 10.6357C100.719 11.9561 101.373 12.813 102.356 12.813Z" fill="#6777FF" />
            <circle cx="6" cy="9" r="6" fill="#6777FF" />
            <path d="M6.70605 9.73438V11.9023H5.60742V9.73438H3.58105V8.65039H5.60742V6.5166H6.70605V8.65039H8.7373V9.73438H6.70605Z" fill="white" />
          </svg>
        </AddGroupButton>
      </PlatformMethodContainer>

      {!!mainPlatformList.length && (
        <>
          <CheckAllCheckboxesContainer
            onClick={() => {
              setSelectedValuesData([]);
            }}
          >

            <FormCheckbox
              name="checkAllCheckboxes"
              label={<FormattedMessage id="Select.All" defaultMessage="Select all" />}
            />

          </CheckAllCheckboxesContainer>

          <Columns>
            {[...(platforms || [])]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(platform => {
                return (
                  <Item key={`platforms.platform-${platform.id}`}>
                    <FormCheckbox
                      name={`platforms.platform-${platform.id}`}
                      label={platform.name}
                    />
                  </Item>
                );
              })}
          </Columns>

        </>
      )}

      <Divider $margin={35} />

      <Modal
        visible={isOpen}
        onHide={() => {
          setIsOpen(false);
          setNewPlatformGroupName("");
          setEditId(null);
        }}
        dismissableMask
        showHeader={false}
        header={null}
        footer={
          <Footer>
            <Button loading={platformGroupsCreating} disabled={isSaveDisabled} onClick={onSaveNewPlatformGroupCheckboxes}>
              <FormattedMessage id="NewRelease.Publish.Save" defaultMessage="Save" />
            </Button>
          </Footer>
        }
      >
        <NewPlatformGroupNameContainer>
          <div style={{ width: "394px" }}>
            <Input
              label={IntlStore.intl.formatMessage({
                id: "NewRelease.Publish.DSPGroupName",
                defaultMessage: "DSP Group Name"
              })}
              name="newPlatformGroupName"
              placeholder={IntlStore.intl.formatMessage({
                id: "NewRelease.Publish.DSPGroupNamePlaceholder",
                defaultMessage: "Enter the name of the DSP Group"
              })}
              value={newPlatformGroupName}
              onChange={e => setNewPlatformGroupName(e.target.value)}
            />
          </div>
        </NewPlatformGroupNameContainer>

        {!!mainPlatformList.length && (
          <>
            <CheckAllCheckboxesContainer>
              <Checkbox
                name="all"
                label={IntlStore.intl.formatMessage({
                  id: "NewRelease.Publish.SelectAllDSPs",
                  defaultMessage: "Select all DSPs"
                })}
                checked={!!newPlatformGroupCheckboxes["all"]}
                onChange={(event: CheckboxChangeEvent) => {
                  const checked = event.target.checked || false;
                  setNewPlatformGroupCheckboxes(prev => {
                    const values: Record<string, boolean> = { all: checked };
                    mainPlatformList.forEach(p => {
                      values[p.id] = checked;
                    });
                    return values;
                  });
                }}
              />
            </CheckAllCheckboxesContainer>

            <Box>
              <Columns>
                {[...regularPlatforms]
                  .sort((a, b) => {
                    if (a.id === YT_ID) return -1;
                    if (b.id === YT_ID) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map(platform => {
                    const isYouTubeCIDSelected =
                      !!newPlatformGroupCheckboxes[YT_ID] || !!selectedValuesData.find(v => v.id === YT_ID);

                    const isDisabled = platform.ugc && platform.id !== YT_ID && !isYouTubeCIDSelected;
                    const checkboxId = `platform-${platform.id}`;
                    const checked = !!newPlatformGroupCheckboxes[platform.id];

                    return (
                      <Item
                        key={platform.id}
                        id={`tooltip-target-${checkboxId}`}
                        style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                      >
                        <Checkbox
                          inputId={checkboxId}
                          name={platform.id.toString()}
                          label={platform.name}
                          checked={checked}
                          disabled={isDisabled}
                          onChange={(event: CheckboxChangeEvent) => {
                            const next = event.target.checked || false;

                            setNewPlatformGroupCheckboxes(prev => {
                              const values: Record<string, boolean> = { ...prev };

                              if (platform.id === YT_ID && !next) {
                                mainPlatformList.forEach(p => {
                                  if (p.ugc && p.id !== YT_ID) values[p.id] = false;
                                });
                              }

                              values[platform.id] = next;

                              values.all = !Object.entries(values).some(([k, v]) => k !== "all" && !v);
                              return values;
                            });
                          }}
                        />
                        {isDisabled && (
                          <Tooltip
                            target={`#tooltip-target-${checkboxId}`}
                            content="YouTube Content ID must be selected first"
                            position="top"
                          />
                        )}
                      </Item>
                    );
                  })}
              </Columns>

              <UGCTitle>UGC platforms</UGCTitle>

              <Columns>
                {[...ugcPlatforms]
                  .sort((a, b) => {
                    if (a.id === YT_ID) return -1;
                    if (b.id === YT_ID) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map(platform => {
                    const isYouTubeCIDSelected =
                      !!newPlatformGroupCheckboxes[YT_ID] || !!selectedValuesData.find(v => v.id === YT_ID);

                    const isDisabled = platform.ugc && platform.id !== YT_ID && !isYouTubeCIDSelected;
                    const checkboxId = `platform-${platform.id}`;
                    const checked = !!newPlatformGroupCheckboxes[platform.id];

                    return (
                      <Item
                        key={platform.id}
                        id={`tooltip-target-${checkboxId}`}
                        style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                      >
                        <Checkbox
                          inputId={checkboxId}
                          name={platform.id.toString()}
                          label={platform.name}
                          checked={checked}
                          disabled={isDisabled}
                          onChange={(event: CheckboxChangeEvent) => {
                            const next = event.target.checked || false;

                            setNewPlatformGroupCheckboxes(prev => {
                              const values: Record<string, boolean> = { ...prev };

                              if (platform.id === YT_ID && !next) {
                                mainPlatformList.forEach(p => {
                                  if (p.ugc && p.id !== YT_ID) values[p.id] = false;
                                });
                              }

                              values[platform.id] = next;

                              values.all = !Object.entries(values).some(([k, v]) => k !== "all" && !v);
                              return values;
                            });
                          }}
                        />
                        {isDisabled && (
                          <Tooltip
                            target={`#tooltip-target-${checkboxId}`}
                            content="YouTube Content ID must be selected first"
                            position="top"
                          />
                        )}
                      </Item>
                    );
                  })}
              </Columns>
            </Box>
          </>
        )}
      </Modal>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 28px 0 0;
`;

const CheckAllCheckboxesContainer = styled.div`
  margin-bottom: 30px;
`;

const PlatformMethodContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 22px;
  width: 100%;

  /* Mobile: title + select + add button should stack */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const PlatformGroupSelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const AddGroupButton = styled.div`
  cursor: pointer;

  @media (max-width: 768px) {
    align-self: flex-start;
  }
`;

const NewPlatformGroupNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 730px;
  margin-bottom: 20px;
  margin-top: 27px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #00000033;
  padding: 12px 16px 0 24px;
  margin: 0 -24px;
`;

const PlatformOption = styled.div`
  display: flex;
  justify-content: space-between;
  width: 260px;
`;

const PlatformOptionLabel = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #000000;
`;

const Columns = styled.div`
  column-count: 4;
  column-gap: 20px;
  margin-bottom: 10px;

  /* Mobile: show as a "table-like" grid that fits the screen */
  @media (max-width: 768px) {
    column-count: initial;
    column-gap: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const Item = styled.div`
  break-inside: avoid;
  margin-bottom: 20px;
  cursor: pointer;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UGCTitle = styled.div`
  color: #000000;
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
`;
